import http from './http';

import { createLogger } from '../utils/logger';

const logger = createLogger('headstoneDetails');
export interface HeadstoneDetailsData {
  id?: string;
  headstonePreference?: string;
  materialPreference?: string;
  sizePreference?: string;
  stylePreference?: string;
  colorPreference?: string;
  inscriptionText?: string;
  epitaph?: string;
  symbolsImagery?: string;
  fontStyle?: string;
  datesFormat?: string;
  additionalEngravings?: string;
  monumentCompany?: string;
  companyContact?: string;
  budgetRange?: string;
  notes?: string;
  isLocked?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateHeadstoneDetailsRequest {
  headstonePreference?: string;
  materialPreference?: string;
  sizePreference?: string;
  stylePreference?: string;
  colorPreference?: string;
  inscriptionText?: string;
  epitaph?: string;
  symbolsImagery?: string;
  fontStyle?: string;
  datesFormat?: string;
  additionalEngravings?: string;
  monumentCompany?: string;
  companyContact?: string;
  budgetRange?: string;
  notes?: string;
}

export interface UpdateHeadstoneDetailsRequest extends CreateHeadstoneDetailsRequest {}

export const headstoneDetailsService = {
  createHeadstoneDetails: async (data: CreateHeadstoneDetailsRequest): Promise<HeadstoneDetailsData> => {
    try {
      logger.debug('[Service] Creating headstone details with data:', data);
      const response = await http.post('/api/headstone-details-info', data);
      logger.debug('[Service] Create response:', response.data);
      return response.data.data;
    } catch (error: any) {
      logger.error('[Service] Error creating headstone details:', error);
      throw error;
    }
  },

  updateHeadstoneDetails: async (id: string, data: UpdateHeadstoneDetailsRequest): Promise<HeadstoneDetailsData> => {
    try {
      logger.debug('[Service] Updating headstone details with ID:', id, 'data:', data);
      const response = await http.patch(`/api/headstone-details-info/${id}`, data);
      logger.debug('[Service] Update response:', response.data);
      return response.data.data;
    } catch (error: any) {
      logger.error('[Service] Error updating headstone details:', error);
      throw error;
    }
  },

  getAllHeadstoneDetails: async (): Promise<HeadstoneDetailsData[]> => {
    try {
      logger.debug('[Service] Getting all headstone details info');
      const response = await http.get('/api/headstone-details-info');
      logger.debug('[Service] Get all response:', response.data);
      return response.data.data || [];
    } catch (error: any) {
      logger.error('[Service] Error getting headstone details info:', error);
      throw error;
    }
  },

  getHeadstoneDetailsById: async (id: string): Promise<HeadstoneDetailsData> => {
    try {
      logger.debug('[Service] Getting headstone details by ID:', id);
      const response = await http.get(`/api/headstone-details-info/${id}`);
      logger.debug('[Service] Get by ID response:', response.data);
      return response.data.data;
    } catch (error: any) {
      logger.error('[Service] Error getting headstone details by ID:', error);
      throw error;
    }
  },

  deleteHeadstoneDetails: async (id: string): Promise<void> => {
    try {
      logger.debug('[Service] Deleting headstone details with ID:', id);
      await http.delete(`/api/headstone-details-info/${id}`);
      logger.debug('[Service] Delete completed');
    } catch (error: any) {
      logger.error('[Service] Error deleting headstone details:', error);
      throw error;
    }
  },

  lockHeadstoneDetails: async (id: string): Promise<void> => {
    try {
      logger.debug('[Service] Locking headstone details with ID:', id);
      await http.post(`/api/headstone-details-info/${id}/lock`);
      logger.debug('[Service] Lock completed');
    } catch (error: any) {
      logger.error('[Service] Error locking headstone details:', error);
      throw error;
    }
  },

  unlockHeadstoneDetails: async (id: string, granteeEmails: string[]): Promise<void> => {
    try {
      logger.debug('[Service] Unlocking headstone details with ID:', id, 'for grantees:', granteeEmails);
      await http.post(`/api/headstone-details-info/${id}/unlock`, { granteeEmails });
      logger.debug('[Service] Unlock completed');
    } catch (error: any) {
      logger.error('[Service] Error unlocking headstone details:', error);
      throw error;
    }
  },

  getHeadstoneDetailsAccessGrants: async (id: string): Promise<any[]> => {
    try {
      logger.debug('[Service] Getting access grants for headstone details ID:', id);
      const response = await http.get(`/api/headstone-details-info/${id}/access-grants`);
      logger.debug('[Service] Access grants response:', response.data);
      return response.data.data || [];
    } catch (error: any) {
      logger.error('[Service] Error getting access grants:', error);
      throw error;
    }
  },

  getLatestHeadstoneDetails: async (): Promise<HeadstoneDetailsData | null> => {
    try {
      const allData = await headstoneDetailsService.getAllHeadstoneDetails();
      if (allData.length > 0) {
        return allData[0];
      }
      return null;
    } catch (error) {
      logger.error('[Service] Error getting latest headstone details:', error);
      throw error;
    }
  }
};